import { Table, type TableColumnsType, type TableProps } from 'antd';
import { useMemo, useState, type ReactNode } from 'react';

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => ReactNode;
  sortValue?: (row: T) => string | number;
  width?: string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  emptyMessage?: string;
  pageSize?: number;
  selectable?: boolean;
  selected?: Set<string>;
  onSelectedChange?: (ids: Set<string>) => void;
  onRowClick?: (row: T) => void;
  initialSort?: { key: string; dir: 'asc' | 'desc' };
}

const compare = (a: string | number, b: string | number) => (typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b)));

/** Ant Table behind the original DataTable contract: client-side sort, pagination, optional selection and row click. */
export function DataTable<T>({ columns, rows, rowKey, loading = false, emptyMessage = 'No records', pageSize: initialPageSize = 25, selectable = false, selected, onSelectedChange, onRowClick, initialSort }: DataTableProps<T>) {
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [page, setPage] = useState(1);
  const antColumns = useMemo<TableColumnsType<T>>(() => columns.map((c) => ({
    key: c.key,
    // A React element title keeps Ant from copying the header text into an aria-label on the th (which would clash with same-named form labels).
    title: <span className="dt-header">{c.header}</span>,
    dataIndex: c.key,
    width: c.width,
    ellipsis: false,
    sorter: c.sortValue ? (a: T, b: T) => compare(c.sortValue!(a), c.sortValue!(b)) : undefined,
    sortDirections: ['ascend', 'descend', 'ascend'],
    showSorterTooltip: false,
    defaultSortOrder: initialSort?.key === c.key ? (initialSort.dir === 'asc' ? 'ascend' : 'descend') : undefined,
    render: (_: unknown, row: T) => (c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '')),
  })), [columns, initialSort]);
  const rowSelection: TableProps<T>['rowSelection'] | undefined = selectable
    ? { selectedRowKeys: [...(selected ?? [])], onChange: (keys) => onSelectedChange?.(new Set(keys.map(String))), columnWidth: 40 }
    : undefined;
  return (
    <div className="data-table-wrap">
      <Table<T>
        className="data-table"
        size="middle"
        columns={antColumns}
        dataSource={rows}
        rowKey={rowKey}
        loading={loading}
        rowSelection={rowSelection}
        locale={{ emptyText: <div className="empty-state">{emptyMessage}</div> }}
        onRow={onRowClick ? (row) => ({ onClick: () => onRowClick(row), style: { cursor: 'pointer' } }) : undefined}
        pagination={{
          current: page, pageSize, total: rows.length, showSizeChanger: true, pageSizeOptions: [10, 25, 50, 100], size: 'small',
          onChange: (p, ps) => { setPage(ps !== pageSize ? 1 : p); setPageSize(ps); },
          showTotal: (total, [from, to]) => <span className="page-info">Page {Math.max(1, Math.ceil(from / pageSize))} of {Math.max(1, Math.ceil(total / pageSize))} · {total} records{total > 0 ? ` (${from}–${to})` : ''}</span>,
        }}
      />
    </div>
  );
}
